import * as PapaParse from 'papaparse'

export interface ParsedSourceCsv {
  urls: string[],
  names: Record<string, string>,
  skipped: number,
  duplicates: number,
  error?: string,
}

export const URL_COLUMNS = ['url', 'source_url', 'source_identifier', 'identifier', 'feed_url', 'feed', 'rss']
export const NAME_COLUMNS = ['name', 'source_name', 'title']

export const SAMPLE_CSV = 'url,name\nhttps://blog.example.com/feed,Example Blog\nhttps://news.example.com/rss,\n'

export const sampleCsvDataUri = (): string => `data:text/csv;charset=utf-8,${encodeURIComponent(SAMPLE_CSV)}`

const normalizeHeader = (header: string): string => header.replace(/^\uFEFF/, '').trim().toLowerCase().replace(/[\s-]+/g, '_')

const findColumn = (headers: string[], candidates: string[]): string =>
  candidates.find((candidate) => headers.includes(candidate))

export const parseSourceCsv = (text: string): ParsedSourceCsv => {
  const empty: ParsedSourceCsv = { urls: [], names: {}, skipped: 0, duplicates: 0 }
  if (!text || !text.trim()) {
    return { ...empty, error: 'CSV file is empty.' }
  }
  const parsed = PapaParse.parse(text, {
    header: true,
    skipEmptyLines: 'greedy',
    transformHeader: normalizeHeader,
  })
  const headers = (parsed.meta.fields || []).map(normalizeHeader)
  const urlColumn = findColumn(headers, URL_COLUMNS)
  if (!urlColumn) {
    return { ...empty, error: `No url column found. Expected a header named one of: ${URL_COLUMNS.join(', ')}.` }
  }
  const nameColumn = findColumn(headers, NAME_COLUMNS)
  const result: ParsedSourceCsv = { ...empty, names: {} }
  const seen = new Set<string>()
  const rows = parsed.data as Record<string, string>[]
  rows.forEach((row) => {
    const url = (row[urlColumn] || '').trim()
    if (!url) {
      result.skipped += 1
      return
    }
    if (seen.has(url)) {
      result.duplicates += 1
      return
    }
    seen.add(url)
    result.urls.push(url)
    const name = nameColumn ? (row[nameColumn] || '').trim() : ''
    if (name) {
      result.names[url] = name
    }
  })
  return result
}

export const fallbackName = (url: string): string => {
  try {
    return new URL(url).hostname
  } catch (err) {
    return ''
  }
}

export const summarizeStatuses = (rows: { status: string }[]): string => {
  const counts: Record<string, number> = {}
  rows.forEach((row) => {
    counts[row.status] = (counts[row.status] || 0) + 1
  })
  return Object.keys(counts).map((status) => `${status}: ${counts[status]}`).join(' | ')
}
